import { useMutation } from '@apollo/client';
import { useAppSelector } from '../redux/store';
import { TRACK_ACTIVITY } from '../graphql/tracking/mutation';

export type ActivityType = 'cartAdd' | 'view' | 'click' | 'registrations';

export const useTrackActivity = () => {
	const [trackActivityMutation] = useMutation(TRACK_ACTIVITY);
	const { isUserLoggedIn } = useAppSelector(state => state.auth);
	const loggedInUserEmail = useAppSelector(
		state => state.auth.loggedInUserDetails?.email
	);

	/**
	 * Track a user activity on a live event
	 * @param eventId - The ID of the live event
	 * @param activityType - The type of activity (cartAdd, view, click, registration)
	 * @param identifier - Unique identifier (email, device ID, etc.) to track the user
	 */
	const trackActivity = async (
		eventId: string,
		activityType: ActivityType,
		identifier?: string
	) => {
		if (!eventId) {
			console.warn('Cannot track activity: Missing eventId');
			return;
		}

		const trackingIdentifier = isUserLoggedIn
			? loggedInUserEmail
			: identifier;

		if (!trackingIdentifier) {
			console.warn('Cannot track activity: Missing identifier');
			return;
		}

		try {
			await trackActivityMutation({
				variables: {
					input: {
						eventId,
						identifier: trackingIdentifier,
						activityType,
					},
				},
			});
		} catch (error) {
			console.error('Error tracking activity:', error);
		}
	};

	/**
	 * Track when a user views a live event
	 */
	const trackViewEvent = (eventId: string, identifier?: string) => {
		return trackActivity(eventId, 'view', identifier);
	};

	/**
	 * Track when a user clicks on something in a live event
	 */
	const trackClickEvent = (eventId: string, identifier?: string) => {
		return trackActivity(eventId, 'click', identifier);
	};

	/**
	 * Track when a user adds a product to cart from a live event
	 */
	const trackCartAddEvent = (eventId: string, identifier?: string) => {
		return trackActivity(eventId, 'cartAdd', identifier);
	};

	/**
	 * Track when a user registers from a live event
	 */
	const trackRegistrationEvent = (eventId: string, identifier?: string) => {
		return trackActivity(eventId, 'registrations', identifier);
	};

	return {
		trackActivity,
		trackViewEvent,
		trackClickEvent,
		trackCartAddEvent,
		trackRegistrationEvent,
	};
};
